import { useState, useEffect, RefObject } from 'react';

interface UseCarouselProps {
  trackRef: RefObject<HTMLDivElement>;
  itemsCount: number;
}

export default function useCarousel({ trackRef, itemsCount }: UseCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScroll, setCanScroll] = useState({ left: false, right: true });

  const updateScrollButtons = () => {
    if (!trackRef.current) return;
    
    setCanScroll({
      left: activeIndex > 0,
      right: activeIndex < itemsCount - 1
    });
  };

  useEffect(() => {
    updateScrollButtons();
    window.addEventListener('resize', updateScrollButtons);
    return () => window.removeEventListener('resize', updateScrollButtons);
  }, [activeIndex]);

  const scrollToIndex = (index: number) => {
    if (!trackRef.current) return;
    
    const container = trackRef.current;
    const isMobile = window.innerWidth < 768;
    const cardWidth = isMobile ? 320 : 400; // Responsive card width
    const gap = 24; // gap-6 = 24px
    
    // Calculate scroll position to center the card
    const scrollLeft = index * (cardWidth + gap);
    
    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });

    setActiveIndex(index);
    updateScrollButtons();
  };

  const handleNext = () => {
    if (activeIndex < itemsCount - 1) {
      scrollToIndex(activeIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (activeIndex > 0) {
      scrollToIndex(activeIndex - 1);
    }
  };

  const handleScroll = () => {
    if (!trackRef.current) return;
    
    const container = trackRef.current;
    const isMobile = window.innerWidth < 768;
    const cardWidth = isMobile ? 320 : 400;
    const gap = 24;
    
    // Calculate current index based on scroll position
    const scrollPosition = container.scrollLeft;
    const newIndex = Math.round(scrollPosition / (cardWidth + gap));
    
    if (newIndex !== activeIndex && newIndex >= 0 && newIndex < itemsCount) {
      setActiveIndex(newIndex);
      updateScrollButtons();
    }
  };

  return {
    activeIndex,
    canScroll,
    handleNext,
    handlePrevious,
    handleScroll,
    scrollToIndex
  };
}